import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  Tabs,
  Tab
} from "@mui/material";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  LineChart,
  Line,
} from "recharts";
import { useApi } from "../api/useApi";
import BackButton from "../components/BackButton";
import { useNavigate } from "react-router-dom";

const AdminDashboard = () => {
  const { request } = useApi();
  const navigate = useNavigate();
  const [visitorCount, setVisitorCount] = useState(0);

  const [tabValue, setTabValue] = useState(0);

  const [signupUsers, setSignupUsers] = useState([]);
  const [contactUsers, setContactUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [contactCount, setContactCount] = useState(0);
  const [filterType, setFilterType] = useState("daily");
  const [chartData, setChartData] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    fetchAnalytics();
    fetchContactRequests();
    fetchVisitorCount();
  }, []);

  const fetchVisitorCount = async () => {
    try {
      const response = await request({
        url: "/analytics/visits/summary",
      });

      setVisitorCount(response.totalVisits || 0);
    } catch (err) {
      console.error("Failed to fetch visitor count", err);
    }
  };

  useEffect(() => {
    if (!signupUsers || signupUsers.length === 0) {
      setChartData([]);
      return;
    }

    const processed = generateChartData(signupUsers, filterType);
    setChartData(processed);
  }, [signupUsers, filterType]);

  const generateChartData = (usersData, type) => {
    const grouped = {};

    // SORT USERS
    const sortedUsers = [...usersData].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    // DAILY
    if (type === "daily") {
      // Get min/max dates
      const startDate = new Date(sortedUsers[0]?.createdAt);
      const endDate = new Date(
        sortedUsers[sortedUsers.length - 1]?.createdAt
      );

      // Create all dates with 0
      const current = new Date(startDate);

      while (current <= endDate) {
        const key = current.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
        });

        grouped[key] = 0;

        current.setDate(current.getDate() + 1);
      }

      // Increment signup count
      sortedUsers.forEach((user) => {
        const date = new Date(user.createdAt);

        const key = date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
        });

        grouped[key] += 1;
      });
    }

    // WEEKLY
    else if (type === "weekly") {
      sortedUsers.forEach((user) => {
        const date = new Date(user.createdAt);

        const firstDay = new Date(date);

        firstDay.setDate(date.getDate() - date.getDay());

        const key = `Week ${firstDay.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
        })}`;

        grouped[key] = (grouped[key] || 0) + 1;
      });
    }

    // MONTHLY
    else if (type === "monthly") {
      sortedUsers.forEach((user) => {
        const date = new Date(user.createdAt);

        const key = date.toLocaleDateString("en-IN", {
          month: "short",
          year: "numeric",
        });

        grouped[key] = (grouped[key] || 0) + 1;
      });
    }

    // QUARTERLY
    else if (type === "quarterly") {
      sortedUsers.forEach((user) => {
        const date = new Date(user.createdAt);

        const quarter =
          Math.floor(date.getMonth() / 3) + 1;

        const key = `Q${quarter} ${date.getFullYear()}`;

        grouped[key] = (grouped[key] || 0) + 1;
      });
    }

    // YEARLY
    else if (type === "yearly") {
      sortedUsers.forEach((user) => {
        const date = new Date(user.createdAt);

        const key = `${date.getFullYear()}`;

        grouped[key] = (grouped[key] || 0) + 1;
      });
    }

    return Object.keys(grouped).map((key) => ({
      name: key,
      signups: grouped[key],
    }));
  };

  const fetchAnalytics = async () => {
    try {
      const response = await request({
        url: "/user/analytics/signup",
      });

      setSignupUsers(response.users || []);
    } catch (err) {
      console.error("Failed to fetch analytics", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchContactRequests = async () => {
    try {
      const response = await request({
        url: "/user/analytics/contact",
      });

      setContactUsers(response.users || []);
      setContactCount(response.contactCount || 0);
    } catch (err) {
      console.error("Failed to fetch contact requests", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "-";

    const digits = phone.replace(/\D/g, "");

    // Adjust based on your supported countries
    const countryCodes = [
      // NANP (North America)
      "1876","1869","1868","1787","1784","1767","1758","1473","1441",
      "1345","1284","1268","1264","1246","1242","1",

      // Europe
      "998","995","994","993","992",
      "995","994","993","992",
      "998",
      "995","994",
      "995",
      "995",
      "995",
      "995",
      "995",
      "995",
      "995",

      "380","381","382","383","385","386","387","389",
      "370","371","372","373","374","375","376","377","378",
      "350","351","352","353","354","355","356","357","358","359",
      "420","421","423",
      "30","31","32","33","34","36","39",
      "40","41","43","44","45","46","47","48","49",

      // Asia
      "850","852","853","855","856","880","886",
      "960","961","962","963","964","965","966","967","968",
      "970","971","972","973","974","975","976","977",
      "81","82","84","86","90","91","92","93","94","95","98",
      "60","61","62","63","64","65","66",

      // Africa
      "211","212","213","216","218","220","221","222","223","224",
      "225","226","227","228","229","230","231","232","233","234",
      "235","236","237","238","239","240","241","242","243","244",
      "245","246","248","249","251","252","253","254","255","256",
      "257","258","260","261","262","263","264","265","266","267",
      "268","269","27",

      // South America
      "500","501","502","503","504","505","506","507","508","509",
      "51","52","53","54","55","56","57","58",
      "591","592","593","594","595","596","597","598","599",

      // Oceania
      "670","672","673","674","675","676","677","678","679",
      "680","681","682","683","685","686","687","688","689",
      "690","691","692"
    ];

    for (const code of countryCodes.sort((a, b) => b.length - a.length)) {
      if (digits.startsWith(code)) {
        return `+${code}-${digits.slice(code.length)}`;
      }
    }

    return `+${digits}`;
  };

  return (
    <Box
      sx={{
        p: 4,
        minHeight: "100vh",
        background: "linear-gradient(to right, #f4f7fb, #eef2ff)",
      }}
    >

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#1b1f2f",
          }}
        >
          Admin Dashboard
        </Typography>

        <Typography
          sx={{
            color: "#666",
            mt: 1,
          }}
        >
          User Sign Up analytics
        </Typography>
      </Box>

      <Paper
        elevation={4}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 4,
          width: 280,
          background:
            "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
          color: "#fff",
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            opacity: 0.9,
          }}
        >
          Number of Visitors
        </Typography>

        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            mt: 1,
          }}
        >
          {visitorCount}
        </Typography>
      </Paper>

      <Box sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) =>
            setTabValue(newValue)
          }
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="Main" />
          <Tab label="Contact" />
        </Tabs>
      </Box>

      {/* Summary Card */}
      {/* <Paper
                elevation={4}
                sx={{
                    p: 3,
                    borderRadius: 4,
                    mb: 4,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background:
                        "linear-gradient(135deg, rgba(90,123,255,1), rgba(124,77,255,1))",
                    color: "#fff",
                }}
            >
                <Box>
                    <Typography variant="h6">Total Contact Requests</Typography>

                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 700,
                            mt: 1,
                        }}
                    >
                        {contactCount}
                    </Typography>
                </Box>

                <Typography
                    sx={{
                        fontSize: 64,
                    }}
                >
                    📈
                </Typography>
            </Paper> */}

      {/* Analytics Header */}
      {tabValue === 0 && (
        <>
        <Box
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 1,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#1b1f2f",
              mb: 2,
            }}
          >
            SignUp Trends
          </Typography>

          <FormControl
            size="small"
            sx={{
              minWidth: 250,
              background: "#fff",
              borderRadius: 2,
            }}
          >
            <InputLabel>Filter</InputLabel>

            <Select
              value={filterType}
              label="Filter"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Full Width Chart */}
        <Paper
          elevation={3}
          sx={{
            p: 3,
            borderRadius: 4,
            mb: 4,
            width: "95%",
            maxWidth: "100%",
          }}
        >
          <ResponsiveContainer width="100%" height={420}>
            {chartData.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Typography>No data available</Typography>
              </Box>
            ) : (
              <LineChart data={chartData}>
                <defs>
                  <linearGradient id="signupGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6ea8fe" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#6ea8fe" stopOpacity={0.1} />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                />

                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12 }}
                />

                <YAxis allowDecimals={false} />

                <Tooltip />

                <Legend />

                <Line
                  type="monotone"
                  dataKey="signups"
                  stroke="#6ea8fe"
                  strokeWidth={3}
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </Paper>
      
      {/* Insights Row */}
      {/* <Grid item xs={12} sm={6} md={3}>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            borderRadius: 4,
          }}
        >
          <Typography
            sx={{
              color: "#666",
              fontSize: 14,
              mb: 1,
            }}
          >
            Total Visitors
          </Typography>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#6ea8fe",
            }}
          >
            {visitorCount}
          </Typography>
        </Paper>
      </Grid> */}
      {/* <Typography variant="h4">Insights</Typography>

      <Grid container spacing={3} sx={{ mb: 10 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 4,
              height: "100%",
            }}
          >
            <Typography
              sx={{
                color: "#666",
                fontSize: 14,
                mb: 1,
              }}
            >
              Total Users
            </Typography>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#1b1f2f",
              }}
            >
              {users.length}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 4,
              height: "100%",
            }}
          >
            <Typography
              sx={{
                color: "#666",
                fontSize: 14,
                mb: 1,
              }}
            >
              Most Active Domain
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#1b1f2f",
              }}
            >
              {
                Object.entries(
                  users.reduce((acc, item) => {
                    acc[item.userDomain] =
                      (acc[item.userDomain] || 0) + 1;

                    return acc;
                  }, {})
                ).sort((a, b) => b[1] - a[1])[0]?.[0] || "-"
              }
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 4,
              height: "100%",
            }}
          >
            <Typography
              sx={{
                color: "#666",
                fontSize: 14,
                mb: 1,
              }}
            >
              Contact Requests
            </Typography>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#6ea8fe",
              }}
            >
              {contactCount}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 4,
              height: "100%",
            }}
          >
            <Typography
              sx={{
                color: "#666",
                fontSize: 14,
                mb: 1,
              }}
            >
              Latest Request
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#1b1f2f",
              }}
            >
              {users?.[0]?.createdAt
                ? new Date(users[0].createdAt).toLocaleDateString()
                : "-"}
            </Typography>
          </Paper>
        </Grid>
      </Grid> */}

      {/* Table */}
      <Typography variant="h4" sx={{ mb: 2 }}>
        SignUp Requests
      </Typography>
      <Paper
        elevation={3}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          mb: 10,
        }}
      >

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 5,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#1b1f2f",
                    }}
                  >
                    <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                      User Name
                    </TableCell>

                    <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                      Email
                    </TableCell>

                    <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                      Role
                    </TableCell>

                    <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                      Organisation
                    </TableCell>

                    <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                      Job Title
                    </TableCell>

                    <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                      Phone Number
                    </TableCell>

                    <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                      Domain
                    </TableCell>

                    <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                      Contact Preference
                    </TableCell>

                    {/* <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                      Created At
                    </TableCell> */}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {signupUsers
                    .slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                    .map((user, index) => (
                      <TableRow
                        key={index}
                        hover
                      >
                        <TableCell>
                          {user.userName || "-"}
                        </TableCell>

                        <TableCell>
                          {user.emailId || "-"}
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={user.userRole || "-"}
                            color="primary"
                            size="small"
                          />
                        </TableCell>

                        <TableCell>
                          {user.organisation || "-"}
                        </TableCell>

                        <TableCell>
                          {user.jobTitle || "-"}
                        </TableCell>

                        <TableCell>
                          {formatPhoneNumber(user.phoneNumber)  || "-"}
                        </TableCell>

                        <TableCell>
                          {user.userDomain || "-"}
                        </TableCell>

                        <TableCell>
                          {user.contactPreference || "-"}
                        </TableCell>

                        {/* <TableCell>
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleDateString()
                            : "-"}
                        </TableCell> */}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={signupUsers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10]}
            />
          </>
        )}
      </Paper>
    </>
    )}

      {/* Table */}
      {tabValue === 1 && (
      <>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Contact Requests
        </Typography>
        <Paper
          elevation={3}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                p: 5,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: "#1b1f2f",
                      }}
                    >
                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                        User Name
                      </TableCell>

                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                        Email
                      </TableCell>

                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                        Role
                      </TableCell>

                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                        Organisation
                      </TableCell>

                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                        Job Title
                      </TableCell>

                      <TableCell sx={{ color: "#fff", fontWeight: 600, width: 120 }}>
                        Phone Number
                      </TableCell>

                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                        Remarks
                      </TableCell>

                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                        Domain
                      </TableCell>

                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                        Contact Preference
                      </TableCell>

                      {/* <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                        Created At
                      </TableCell> */}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {contactUsers
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((user, index) => (
                        <TableRow
                          key={index}
                          hover
                        >
                          <TableCell>
                            {user.userName || "-"}
                          </TableCell>

                          <TableCell>
                            {user.emailId || "-"}
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={user.userRole || "-"}
                              color="primary"
                              size="small"
                            />
                          </TableCell>

                          <TableCell>
                            {user.organisation || "-"}
                          </TableCell>

                          <TableCell>
                            {user.jobTitle || "-"}
                          </TableCell>

                          <TableCell>
                            {formatPhoneNumber(user.phoneNumber) || "-"}
                          </TableCell>

                          <TableCell>
                            {user.remarks?.length > 100 ? (
                              <Tooltip
                                title={user.remarks}
                                arrow
                                placement="top"
                              >
                                <span>
                                  {`${user.remarks.substring(0, 100)}...`}
                                </span>
                              </Tooltip>
                            ) : (
                              <span>{user.remarks || "-"}</span>
                            )}
                          </TableCell>

                          <TableCell>
                            {user.userDomain || "-"}
                          </TableCell>

                          <TableCell>
                            {user.contactPreference || "-"}
                          </TableCell>

                          {/* <TableCell>
                            {user.createdAt
                              ? new Date(user.createdAt).toLocaleDateString()
                              : "-"}
                          </TableCell> */}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={contactUsers.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10]}
              />
            </>
          )}
        </Paper>
      </>
      )}
    </Box>
  );
};

export default AdminDashboard;